/*
 * Header dropdown: <wp-nav-drop> (snippets/header-nav-item.liquid), modelled on balmoralrunning.com.
 *
 * Hovering the parent opens it; the child links stagger in beneath it and the rest of the header fades
 * back (both CSS, in assets/app-tailwind.css). Click toggles too, for touch and keyboard. Esc closes and
 * returns focus to the toggle; focus or a click leaving the element closes it. Only one is open at a time.
 *
 * While open it sets data-nav-open on [data-header] (drives the dimming) and the panel's height as
 * --wp-drop-h (sizes the cream sheet behind the links on solid headers). Functional, not decorative, so it
 * isn't gated on prefers-reduced-motion; the CSS drops the motion instead.
 */
(function () {
  if (customElements.get('wp-nav-drop')) return;

  const CLOSE_DELAY = 150; // ms grace period for the pointer to travel from the toggle into the list

  class WpNavDrop extends HTMLElement {
    constructor() {
      super();
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.toggle = this.querySelector('.wp-navdrop__toggle');
      this.panel = this.querySelector('.wp-navdrop__panel');
      this.header = this.closest('[data-header]');
      if (!this.toggle || !this.panel) return;

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      this.toggle.addEventListener('click', this.onToggleClick);
    }

    disconnectedCallback() {
      this.setOpen(false);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    setOpen(open) {
      clearTimeout(this.closeTimer);
      if (open === this.isOpen) return;

      if (open) {
        // One open at a time.
        document.querySelectorAll('wp-nav-drop[data-open]').forEach((other) => other !== this && other.setOpen(false));
        this.setAttribute('data-open', '');
        document.addEventListener('click', this.onDocumentClick);
      } else {
        this.removeAttribute('data-open');
        document.removeEventListener('click', this.onDocumentClick);
      }
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (this.header) {
        const anyOpen = !!this.header.querySelector('wp-nav-drop[data-open]');
        this.header.setAttribute('data-nav-open', anyOpen ? 'true' : 'false');
        if (open) this.header.style.setProperty('--wp-drop-h', `${this.panel.scrollHeight}px`);
      }
    }

    onPointerEnter(event) {
      if (event.pointerType !== 'mouse') return;
      this.setOpen(true);
    }

    onPointerLeave(event) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.setOpen(false), CLOSE_DELAY);
    }

    onToggleClick() {
      this.setOpen(!this.isOpen);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      event.stopPropagation();
      this.setOpen(false);
      this.toggle.focus();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      // relatedTarget is null when focus leaves the window or (Safari) a click doesn't move focus; the
      // document click handler covers clicks, so only close here when focus actually went somewhere else.
      if (event.relatedTarget) this.setOpen(false);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.setOpen(false);
    }
  }

  customElements.define('wp-nav-drop', WpNavDrop);
})();
